import React, { useMemo, useState } from 'react';
import { history } from 'umi';
import { useMemoizedFn } from 'ahooks';
import usePageLog, { reportBtnOperationLog } from '@gp/hooks/usePageLog';
import useDPSReportMomentLocale from '../../_hooks/useDPSReportMomentLocale';
import { fetchDPSReportInitialData, fetchCommonProjectsMilestones, editDPSReport } from '@gp/services/dpsReport/v2';
import { fetchProjectsMilestones } from '../services';
import { addUuidBatch, addDays, messageTips } from '@gp/utils/common';
// @ts-ignore
import { storage } from '@gp/utils/utils';
import { getWeekDateOfDPSReport, getCommonRepeatCheckKey } from '../../utils';
import { refreshCollection } from '../utils/refreshData';
import { BoolFlag } from '@gp/enums/common';
import { DPS_REPORT_STORAGE_KEY } from '@gp/constants/storageKeys';

import type { IProjectSimpleDTO } from '@gp/types/global';
import type { IDPSReportFilter, IDPSReportHelperVO, IDPSReportContextDTO } from '@gp/types/dpsReport/common';
import type { IDPSReportProjectsMilestonesVOV2 } from '@gp/types/dpsReport/v2';
import type { IDPSReportFormValue } from '../types';

const REMOVE_KEYS = ['mock_id', 'data_type', 'details'];

const commonFilter = <T extends IDPSReportContextDTO>(e?: T): boolean => {
  const { project_id: projectId, milestone_id: milestoneId } = e || {};
  return !!projectId && !!milestoneId;
};

const changesFilter = <T extends IProjectSimpleDTO>(e?: T): boolean => !!e?.project_id;

const miscellaneousFilter = <T extends IProjectSimpleDTO>(e?: T): boolean => !!e?.project_id;

const formatReportModuleParams = (list?: any[], filter = commonFilter): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.filter(filter).map((item) => {
    const keys = Object.keys(item).filter((key) => !REMOVE_KEYS.includes(key));
    return keys.reduce<any>((acc, key) => {
      acc[key] = item[key];
      return acc;
    }, {});
  });
};

export default function useOperateDPSReport(props: {
  pageId: string;
  pageLogOthers?: {
    uidType: string;
    uid: string;
    extInfo: string;
  };
  confirmBtnId: string;
  publishBtnId: string;
  refreshHightlightsBtnId: string;
  refreshCalendarBtnId: string;
  // 获取初始化筛选器数据
  getInitialFilter: () => IDPSReportFilter;
  // 更新周报数据
  updateReportPage: (filter: IDPSReportFilter) => void;
  // 获取当前周报的value
  getReport?: () => IDPSReportFormValue;
  // 设置当前周报的value
  setReport?: (payload: Partial<IDPSReportFormValue>) => void;
}) {
  const {
    pageId,
    pageLogOthers,
    confirmBtnId,
    publishBtnId,
    refreshHightlightsBtnId,
    refreshCalendarBtnId,
    getInitialFilter,
    updateReportPage,
    getReport,
    setReport,
  } = props;

  const [isGuide, setIsGuide] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isHightlightsInnerLoading, setIsHightlightsInnerLoading] = useState(false);
  const [isCalendarInnerLoading, setIsCalendarInnerLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitType, setSubmitType] = useState<BoolFlag>(BoolFlag.False);

  const [filter, setFilter] = useState<IDPSReportFilter>({
    start_date: '',
    end_date: '',
  });

  const [projectsMilestones, setProjectsMilestones] = useState<IDPSReportProjectsMilestonesVOV2>({
    hightlights: [],
    changes: [],
    miscellaneous: [],
    calendar: [],
  });

  const reportHelper = useMemo<IDPSReportHelperVO | null>(() => {
    const { start_date: startDate, end_date: endDate } = filter || {};
    if (!startDate || !endDate) return null;
    const {
      startDateM: startDateMDPSReportWeek,
      startDate: startDateDPSReportWeek,
      endDate: endDateDPSReportWeek,
    } = getWeekDateOfDPSReport(addDays(startDate, 1), addDays(endDate, 1));
    return {
      // 开始日期（DPS周报要求周一作为一周的开始日期）
      start_date_dps_report_week: startDateDPSReportWeek,
      // 结束日期（DPS周报要求周五作为一周的结束日期）
      end_date_dps_report_week: endDateDPSReportWeek,
      // 年份（当前周的年份）
      year: startDateMDPSReportWeek?.get('year') || Number.NaN,
      // 周数（当前周是当前年中的第几周）
      weeks_in_year: startDateMDPSReportWeek?.weeks() || Number.NaN,
    };
  }, [filter]);

  const updateProjectsMilestones = useMemoizedFn(async (filter: IDPSReportFilter) => {
    const res = await fetchProjectsMilestones(filter);
    const { hightlights, changes, miscellaneous, calendar } = res?.data || {};
    const projectsMilestonesTmp = {
      ...projectsMilestones,
      hightlights: hightlights || [],
      changes: changes || [],
      miscellaneous: miscellaneous || [],
      calendar: calendar || [],
    };
    setProjectsMilestones(projectsMilestonesTmp);
  });

  const updateReport = useMemoizedFn(async (filter: IDPSReportFilter) => {
    updateProjectsMilestones(filter);
    updateReportPage(filter);
  });

  const handleRefreshDataCommon = useMemoizedFn(
    async (params: {
      moduleKey: 'hightlights' | 'calendar';
      moduleIgnoreKeys: string[];
      getRepeatCheckKey: (payload: any) => string;
      setIsModuleLoading: React.Dispatch<React.SetStateAction<boolean>>;
    }) => {
      const { moduleKey, moduleIgnoreKeys, getRepeatCheckKey, setIsModuleLoading } = params || {};

      try {
        setIsModuleLoading(true);
        const currentModuleData = getReport?.()[moduleKey] || [];
        const initialRes = await fetchDPSReportInitialData(filter);
        const initialModuleData = addUuidBatch(initialRes?.data?.[moduleKey]);

        const projectsMilestonesRes = await fetchCommonProjectsMilestones(filter);
        const moduleProjectsMilestones = projectsMilestonesRes?.data?.[moduleKey] || [];
        const projectsMilestonesTmp = {
          ...projectsMilestones,
          [moduleKey]: moduleProjectsMilestones,
        };
        const refreshedModuleData = refreshCollection({
          current: currentModuleData,
          initial: initialModuleData,
          projectsMilestones: moduleProjectsMilestones,
          ignoreKeys: moduleIgnoreKeys,
          getRepeatCheckKey,
        });
        setReport?.({
          [moduleKey]: refreshedModuleData,
        });
        setProjectsMilestones(projectsMilestonesTmp);
      } finally {
        setIsModuleLoading(false);
      }
    },
  );

  const handleHightlightsRefreshData = useMemoizedFn(async () => {
    handleRefreshDataCommon({
      moduleKey: 'hightlights',
      moduleIgnoreKeys: [],
      getRepeatCheckKey: getCommonRepeatCheckKey,
      setIsModuleLoading: setIsHightlightsInnerLoading,
    });
    reportBtnOperationLog(refreshHightlightsBtnId);
  });

  const handleCalendarRefreshData = useMemoizedFn(async () => {
    handleRefreshDataCommon({
      moduleKey: 'calendar',
      moduleIgnoreKeys: ['status'],
      getRepeatCheckKey: getCommonRepeatCheckKey,
      setIsModuleLoading: setIsCalendarInnerLoading,
    });
    reportBtnOperationLog(refreshCalendarBtnId);
  });

  const handleSubmit = useMemoizedFn(
    async (
      report: IDPSReportFormValue,
      isPublish: boolean,
      cfg: {
        reportId?: string;
        tips: string;
      },
    ) => {
      try {
        const { reportId, tips } = cfg || {};
        const { start_date: startDate, end_date: endDate } = filter || {};
        const { comment, hightlights, changes, miscellaneous, calendar } = report || {};
        const submitTypeTmp = isPublish ? BoolFlag.True : BoolFlag.False;
        const params = {
          report_id: reportId || '',
          is_publish: submitTypeTmp,
          start_date: startDate || '',
          end_date: endDate || '',
          comment: comment || '',
          hightlights: formatReportModuleParams(hightlights),
          changes: formatReportModuleParams(changes, changesFilter),
          miscellaneous: formatReportModuleParams(miscellaneous, miscellaneousFilter),
          calendar: formatReportModuleParams(calendar),
        };
        setIsSubmitting(true);
        setSubmitType(submitTypeTmp);
        const res = await editDPSReport(params);
        messageTips('success', tips);
        setIsGuide(false);
        storage.setSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_ID, res?.data?.report_id || '');
        history.goBack();
      } catch (exception: any) {
        messageTips('error', exception?.message || '');
      } finally {
        setIsSubmitting(false);
        reportBtnOperationLog(isPublish ? publishBtnId : confirmBtnId);
      }
    },
  );

  const init = () => {
    const initialFilter = { ...filter, ...getInitialFilter() };
    if (!!initialFilter.start_date && !!initialFilter.end_date) {
      updateReport(initialFilter);
    }
    setFilter(initialFilter);
  };

  useDPSReportMomentLocale(init);

  usePageLog({ pageId, ...pageLogOthers });

  return {
    isGuide,
    isLoading,
    setIsLoading,
    isHightlightsInnerLoading,
    isCalendarInnerLoading,
    isSubmitting,
    submitType,
    reportHelper,
    projectsMilestones,
    filter,
    setFilter,
    updateReport,
    onHightlightsRefreshData: handleHightlightsRefreshData,
    onCalendarRefreshData: handleCalendarRefreshData,
    onSubmit: handleSubmit,
  };
}
